<template>
  <v-container fluid fill-height>
    <v-card class="rounded-lg pa-2 fullscreen" flat>
      <div class="d-flex align-center justify-space-between">
        <div class="rounded-lg pa-1 pageTitle font-weight-black text-h5">
          Dashboard
        </div>
        <span v-if="isAuth" class="d-flex justify-space-between align-center">
          <v-icon class="mr-2 cursor-pointer" @click="getPageData">mdi-refresh</v-icon>
          {{ expIn }}
        </span>
      </div>
      <v-divider class="my-3 mx-2" />
      <div v-if="isAuth" class="ma-2">
        <p class="dashboard-row pa-3">
          <StatusCard
            title="啟用的標的物 / 標的物總數"
            :value="`${siteNum.actived} / ${siteNum.total}`"
            description=""
          />
          <StatusCard
            title="攝影機總數"
            :value="deviceNum"
            description=""
          />
          <StatusCard
            title="防護總時數"
            :value="protectTime"
            description=""
          />
          <StatusCard
            title="未結案數量"
            :value="unsolvedNum"
            description=""
          />
        </p>
        <v-row>
          <v-col>
            <StatusCard
              title="近七日發報數量"
              description=""
            >
              <apexchart
                v-if="alarm_num.series.length"
                :options="alarm_num.options"
                :series="alarm_num.series"
                width="750"
                height="465"
              />
            </StatusCard>
          </v-col>
          <v-col>
            <StatusCard
              title="發報類別"
              description=""
            >
              <apexchart
                :options="alarm_type.options"
                :series="alarm_type.series"
                width="750"
                height="465"
              />
            </StatusCard>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <StatusCard
              title="近七日發報熱區圖"
              description=""
            >
              <MapHeat :data="alarm_coordinates" style="width: 750px; height: 500px;" />
            </StatusCard>
          </v-col>
          <v-col>
            <StatusCard
              title="標的物分布圖"
              description=""
            >
              <MapMarkerGroup :data="site_coordinates" style="width: 750px; height: 500px;" />
            </StatusCard>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </v-container>
</template>
<script>
import Service from '../service/service'
import StatusCard from '../components/StatusCard.vue'
import MapHeat from '../components/Dashboard/MapHeat.vue'
import MapMarkerGroup from '../components/Dashboard/MapMarkerGroup.vue'

export default {
  // eslint-disable-next-line
  components: { StatusCard, MapHeat, MapMarkerGroup },
  name: 'Dashboard',
  data() {
    return {
      isLoading: false,
      statisticList: [],
      numIntl: new Intl.NumberFormat(navigator.language || 'en', { notation: 'compact', maximumFractionDigits: 2 }),
      timeIntl: new Intl.RelativeTimeFormat(navigator.language || 'en', { style: 'short', numeric: 'auto' }),
      now: Date.now(),
      expAt: 0
    }
  },
  computed: {
    user() {
      const json = this.$store.getters.getUser
      try {
        return JSON.parse(json)
      }
      catch (e) {
        return json
      }
    },
    isAuth() {
      return [1, 4].includes(this.user.roleId)
    },
    siteNum() {
      const data = this.statisticList['site_num']?.value[0] ?? {}
      const total = this.numIntl.format(data.removed + data.actived)
      const actived = this.numIntl.format(data.actived)

      return { actived, total }
    },
    deviceNum() {
      const value = this.statisticList['device_num']?.value[0].deviceNum
      return this.numIntl.format(value)
    },
    protectTime() {
      const value = this.statisticList['protect_time']?.value.reduce((acc, cur) => {
        return acc + cur.HOUR_ACC
      }, 0)

      return this.numIntl.format(value)
    },
    unsolvedNum() {
      const value = this.statisticList['unsolved_info']?.value.find(d => d.Flag === 'N')?.num ?? 0
      return this.numIntl.format(value)
    },
    alarm_num() {
      const data = this.statisticList['alarm_info']?.value ?? []

      const dates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date()
        d.setDate(d.getDate() - i - 1)
        return d.toISOString().slice(0, 10)
      })
        .reverse()

      const area = data.reduce(
        (acc, cur) => acc.add(cur.area_nm),
        new Set()
      )

      const opt =  {
        options: {
          chart: {
            type: 'bar',
            stacked: true
          },
          xaxis: { categories: [...dates] },
        },
        series: [...area].map(area => ({
          data: data
            .filter(d => d.area_nm === area)
            .map(d => ({ x: d.date.slice(0, 10), y: d.num })) ?? [],
          name: area
        }))
      }

      return opt
    },
    alarm_type() {

      const labels = []
      const data = []
      let sum = 0
      let other = 0

      for (const { Name, amount } of this.statisticList['status_info']?.value ?? []) {
        sum += amount
        
        if (amount / sum < 0.01) {
          other += amount
          continue
        }

        labels.push(Name)
        data.push(amount)
      }

      labels.push('其他')
      data.push(other)

      return {
        options: {
          chart: {
            type: 'pie',
          },
          labels
        },
        series: data
      }
    },
    expIn() {
      const time =  Math.floor((this.expAt - this.now) / 1000)
      return this.timeIntl.format(time, 'seconds')
    },
    site_coordinates() {
      const value = this.statisticList['site_coordinate']?.value.map(d => ([d.data_value, d.data_remark, 1]))
      return value ?? []
    },
    alarm_coordinates() {
      const sum = this.statisticList['alarm_coordinate']?.value.reduce((acc, cur) => acc + cur.num, 0)
      const value = this.statisticList['alarm_coordinate']?.value.map(d => ([d.data_value, d.data_remark, Math.max(d.num / sum, 0.5)]))
      return value ?? []
    }
  },
  mounted() {
    if (!this.isAuth) {
      return;
    }

    this.interval = setInterval(() => {
      this.now = Date.now()

      if (this.expAt - this.now < 0) {
        this.getPageData()
        this.expAt = this.now + 60000
      }
    }, 100)
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  methods: {
    getPageData() {
      this.isLoading = true
      Service.getPageData({
        url: 'statistic_info/getPageData'
      }, true)
        .then((response) => {
          if (response.status !== 200 || response.data.status !== 0) {
            return this.showAlert('error', response.data.message)
          } 

          this.total = response.data.total
          this.statisticList = response.data.rows.reduce((acc, cur) => {
            const value = JSON.parse(cur.value)
            acc[cur.name] = Object.assign(cur, { value })

            return acc
          }, {})
        })
        .finally(() => {
          this.isLoading = false
          this.expAt = this.now + 60000
        })
    },
    showAlert (type, message) {
      var alert = {
        alertType: null,
        alertMessage: '',
        alertIcon: null
      }
      if (type === 'success') {
        alert.alertType = 'success'
        alert.alertIcon = 'mdi-check-circle'
      } else {
        alert.alertType = 'error'
        alert.alertIcon = 'mdi-alert-circle'
      }
      alert.alertMessage = message
      this.$emit('setAlert', alert)
    },
  }
}
</script>

<style scoped>
.v-card {
  opacity: 0.8;
}

.fullscreen {
  width: 100%;
  height: 100%;
}

.pageTitle {
  width: 200px;
}

.dashboard-row {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 16px;
}
</style>
